<template>
  <component :is="embedded ? 'BcFlat' : 'BcSheet'"
             :step="step"
             :total="total"
             :show-back="showBack"
             :locked="locked"
             :title="$t('broadcast.pages.title')"
             @back="$emit('back')"
             @close="$emit('close')">
    <div class="search-box mb-2">
      <i class="fal fa-search"></i>
      <input v-model="query"
             class="form-control"
             :placeholder="$t('broadcast.pages.search')"
             autocomplete="off" />
    </div>
    <div class="bc-rowbtns">
      <button type="button"
              class="btn btn-outline-primary"
              @click="selectAll">{{ $t("broadcast.pages.select_all") }}</button>
      <button type="button"
              class="btn btn-default"
              @click="clear">{{ $t("broadcast.pages.clear") }}</button>
    </div>
    <p v-if="syncing"
       class="bc-note">{{ $t("broadcast.pages.syncing_note") }}</p>
    <div class="bc-pagelist">
      <template v-for="page in visible">
        <button v-if="page.eligible"
                :key="page.pageId"
                type="button"
                class="bc-pagerow"
                :class="{ '--on': isOn(page) }"
                :aria-pressed="String(isOn(page))"
                @click="toggle(page)">
          <span class="bc-check"
                :class="{ '--on': isOn(page) }"
                aria-hidden="true"><i v-if="isOn(page)"
               class="fas fa-check"></i></span>
          <PageAvatar :page="avatarOf(page)"
                      size="sm" />
          <span class="bc-pagename">{{ page.name }}</span>
          <span class="bc-pagestate">
            <span class="bc-chip"
                  :class="chipClass(page)">{{ chipText(page) }}</span>
          </span>
        </button>
        <div v-else
             :key="page.pageId"
             class="bc-pagerow --off"
             role="group"
             :aria-label="page.name">
          <PageAvatar :page="avatarOf(page)"
                      size="sm" />
          <span class="bc-pagename">{{ page.name }}</span>
          <span class="bc-pagestate">
            <span v-if="page.blockTh"
                  class="bc-chip --danger">{{ page.blockTh }}</span>
          </span>
          <button type="button"
                  class="btn btn-primary btn-sm"
                  @click="$emit('reconnect', page)">{{ $t("broadcast.pages.reconnect") }}</button>
        </div>
      </template>
    </div>
    <template v-if="!embedded"
              #foot>
      <span class="bc-sticky-n">{{ $t("broadcast.pages.selected", { n: selectedCount.toLocaleString("en-US") }) }}</span>
      <button type="button"
              class="btn btn-primary btn-lg"
              :disabled="!selectedCount"
              @click="$emit('apply', value)">{{ $t("broadcast.pages.next") }}</button>
    </template>
  </component>
</template>

<script>
import BcSheet from "@/components/derived/broadcast/BcSheet";
import BcFlat from "@/components/derived/broadcast/BcFlat";
import PageAvatar from "@/components/derived/messenger/PageAvatar";

// "ส่งจากเพจไหน?": pick the Pages a broadcast goes out from. `pages` is GET /api/broadcast/pages
// (pageId, name, picture, eligible, blockTh, sync.chipTh); `value` is the array of selected pageIds.
// Emits input (new array) on every change, apply (array) on ถัดไป, back, close, reconnect (page).
// Labels (chipTh, blockTh) come from the API; nothing here decides why a Page is blocked.
export default {
  name: "PagePickerSheet",
  components: { BcSheet, BcFlat, PageAvatar },
  props: {
    pages: { type: Array, required: true },
    value: { type: Array, default: () => [] },
    step: { type: Number, default: 1 },
    total: { type: Number, default: 0 },
    showBack: { type: Boolean, default: false },
    locked: { type: Boolean, default: false },
    // Render inside a sheet that is already open: no popup, no footer (the host shows ถัดไป itself)
    embedded: { type: Boolean, default: false },
  },
  data() {
    return { query: "" };
  },
  computed: {
    visible() {
      const q = this.query.trim().toLowerCase();
      return q ? this.pages.filter((p) => String(p.name || "").toLowerCase().includes(q)) : this.pages;
    },
    eligible() {
      return this.pages.filter((p) => p.eligible);
    },
    // Only eligible Pages count: a stale id in `value` must not enable ถัดไป
    selectedCount() {
      const ids = new Set(this.value.map(String));
      return this.eligible.filter((p) => ids.has(String(p.pageId))).length;
    },
    syncing() {
      return this.eligible.some((p) => p.sync && p.sync.state === "syncing");
    },
  },
  methods: {
    isOn(page) {
      return this.value.map(String).includes(String(page.pageId));
    },
    toggle(page) {
      const id = String(page.pageId);
      this.$emit("input", this.isOn(page) ? this.value.filter((v) => String(v) !== id) : this.value.concat(id));
    },
    selectAll() {
      this.$emit("input", this.eligible.map((p) => String(p.pageId)));
    },
    clear() {
      this.$emit("input", []);
    },
    avatarOf(page) {
      return { pageId: page.pageId, name: page.name, pictureUrl: page.picture || null };
    },
    chipText(page) {
      return (page.sync && page.sync.chipTh) || this.$t("broadcast.pages.ready");
    },
    chipClass(page) {
      return page.sync && page.sync.state === "syncing" ? "--info" : "--success";
    },
  },
};
</script>

<style lang="scss" scoped>
.search-box {
  position: relative;

  .fal {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
  }

  .form-control {
    padding-left: 2.1rem;
  }
}

.bc-rowbtns {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .btn {
    flex: 1 1 auto;
  }
}

.bc-note {
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.75rem;
}

.bc-pagelist {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.bc-pagerow {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  min-height: 60px;
  padding: 0.6rem 0.75rem;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  color: #333;
  text-align: left;

  &:hover {
    border-color: $brand-blue-mid;
  }

  &.--on {
    border-color: $brand-blue-mid;
    background: rgba($brand-blue-mid, 0.05);
  }

  &.--off {
    cursor: default;
    background: #fdf6f6;

    &:hover {
      border-color: #eee;
    }

    .btn {
      flex: 0 0 auto;
      min-width: 44px;
    }
  }
}

.bc-check {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid #b5b5b5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.8rem;

  &.--on {
    background: $brand-blue-mid;
    border-color: $brand-blue-mid;
  }
}

.bc-pagename {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #333;
}

.bc-pagestate {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.bc-chip {
  display: inline-block;
  padding: 0.1rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.4;

  &.--success {
    background: rgba(48, 196, 76, 0.15);
    color: #1a6e2e;
  }

  &.--info {
    background: rgba(39, 170, 224, 0.15);
    color: #1d6f94;
  }

  &.--danger {
    background: rgba(246, 116, 116, 0.18);
    color: #c62828;
    text-align: right;
  }
}

.bc-sticky-n {
  font-weight: 700;
  font-size: 1.05rem;
  color: #333;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .bc-pagerow {
    flex-wrap: wrap;
  }

  .bc-pagename {
    flex: 1 1 100%;
    order: 3;
    white-space: normal;
  }
}

body.--dark {
  .search-box .form-control {
    background: #4b4b4b;
    border-color: #555;
    color: #e6e6e6;
  }

  .bc-note {
    color: #c4c4c4;
  }

  .bc-pagerow {
    background: #4b4b4b;
    border-color: #555;
    color: #e6e6e6;

    &:hover,
    &.--on {
      border-color: #8cc8f5;
    }

    &.--on {
      background: rgba(140, 200, 245, 0.08);
    }

    &.--off {
      background: #5a4545;
    }
  }

  .bc-check.--on {
    background: #1c75bb;
    border-color: #1c75bb;
  }

  .bc-pagename,
  .bc-sticky-n {
    color: #e6e6e6;
  }

  .bc-chip {
    &.--success {
      color: #8fe3a0;
    }

    &.--info {
      color: #8cd6f5;
    }

    &.--danger {
      color: #ff9d9d;
    }
  }

  .bc-rowbtns .btn-outline-primary {
    color: #8cc8f5;
    border-color: #8cc8f5;
  }
}
</style>

